/**
 * The command palette (Ctrl+K / Cmd+K). **INBOX scope item 21.**
 *
 * ┌─ ⛔ WHAT IT HOLDS, AND THE TWO THINGS IT DELIBERATELY DOES NOT ─────────────┐
 * │ Navigation to a contact, plus `snooze` and `assign` on the OPEN            │
 * │ conversation. `palette-view.ts` carries the ranking rule and the action    │
 * │ vocabulary; this owns the words, the keys and the focus.                   │
 * │                                                                            │
 * │ ⛔ NO `tag`, NO `add task`. `Build_Order:484` names four safe mutations and │
 * │ two of them have no table, no column and no producer anywhere - both are   │
 * │ CRM-BUILD's. The item names things a later phase owns, which is the same   │
 * │ shape as the panel's booking blocks.                                       │
 * │                                                                            │
 * │ ⛔ NO HANDOFF ROW. `Build_Order:486` requires *"the last row is ALWAYS a    │
 * │ handoff to full search"*, whose stated reason is that behaviour must not   │
 * │ vary WITH THE QUERY. It says nothing about build state, and item 22 does   │
 * │ not exist - `web/src/routing.ts` parses `inbox` and `elsewhere`, with no   │
 * │ `/search`. A present-but-inert row would be a dead affordance bought for   │
 * │ nothing, so it is a NAMED GAP under item 11's no-empty-cards rule.         │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ MESSAGES NEVER APPEAR HERE, which is also `Build_Order:486`, and it is why
 * item 20's message half ships with the search page instead of with this.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type PaletteAction,
  type PaletteRow,
  paletteEmpty,
  rankPalette,
} from './palette-view.js'
import { type ContactHit, searchContactsApi } from '../data/inbox-api.js'

/** Mirrors `MIN_QUERY` in the service. The route answers shorter queries with an empty list. */
const MIN_QUERY = 3

const ACTION_LABEL: Record<PaletteAction['kind'], string> = {
  snooze: 'Snooze This Conversation',
  assign: 'Assign This Conversation',
}

export type CommandPaletteProps = {
  open: boolean
  onClose: () => void
  /** The conversation the actions act on, or `null` when none is open. */
  conversationId: string | null
  onOpenConversation: (id: string) => void
  /** The header owns both action surfaces; the palette asks it to open one. */
  onAction: (action: PaletteAction['kind']) => void
  /** TEST SEAM, exactly like `InboxShell`'s socket. Production passes nothing. */
  search?: typeof searchContactsApi
}

export function CommandPalette({
  open,
  onClose,
  conversationId,
  onOpenConversation,
  onAction,
  search = searchContactsApi,
}: CommandPaletteProps): React.JSX.Element | null {
  const [query, setQuery] = useState('')
  const [contacts, setContacts] = useState<ContactHit[]>([])
  const [loading, setLoading] = useState(false)
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Opening always starts clean. A palette that reopened on the last query
  // would act on a search somebody has forgotten they ran.
  useEffect(() => {
    if (!open) return
    setQuery('')
    setContacts([])
    setCursor(0)
    inputRef.current?.focus()
  }, [open])

  /**
   * ⛔ THE RESPONSE IS DISCARDED IF THE QUERY HAS MOVED ON. Keystrokes race:
   * `car` can answer after `carol` and repaint the older result over the newer
   * one, which reads as the palette ignoring what you typed. The guard is the
   * query itself rather than a timer, so it is exact rather than tuned.
   */
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < MIN_QUERY) {
      setContacts([])
      setLoading(false)
      return
    }
    let live = true
    setLoading(true)
    void search(q)
      .then((res) => {
        if (!live) return
        setContacts(res.contacts)
        setCursor(0)
      })
      .catch(() => {
        if (live) setContacts([])
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [open, query, search])

  const rows: PaletteRow[] = rankPalette(query, contacts)
  /**
   * ⚠️ ACTIONS ARE HIDDEN WITH NOTHING OPEN rather than shown and disabled.
   * "Snooze this conversation" with no conversation is a sentence about nothing,
   * and a disabled row still costs a keystroke to skip past.
   */
  const usable = conversationId === null ? rows.filter((r) => r.tier === 'record') : rows

  const run = useCallback(
    (row: PaletteRow) => {
      if (row.tier === 'action') {
        onAction(row.action.kind)
      } else if (row.contact.conversationId !== null) {
        onOpenConversation(row.contact.conversationId)
      } else {
        // A contact with no conversation has nothing to open. See the service:
        // the schema permits it and CRM-BUILD will produce it.
        return
      }
      onClose()
    },
    [onAction, onOpenConversation, onClose],
  )

  if (!open) return null

  const empty = paletteEmpty({ query, minQuery: MIN_QUERY, loading, rows: usable })

  return (
    <div
      className="palette__scrim"
      data-testid="palette"
      /* Clicking away closes it. `role` is on the dialog, not here. */
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <input
          ref={inputRef}
          className="palette__input"
          data-testid="palette-input"
          value={query}
          placeholder="Search people, or type a command"
          aria-label="Search people, or type a command"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose()
              return
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setCursor((c) => Math.min(c + 1, Math.max(usable.length - 1, 0)))
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              setCursor((c) => Math.max(c - 1, 0))
              return
            }
            if (e.key === 'Enter') {
              e.preventDefault()
              const row = usable[cursor]
              if (row !== undefined) run(row)
            }
          }}
        />

        {empty.kind === 'hasRows' ? (
          <ul className="palette__rows" role="listbox" data-testid="palette-rows">
            {usable.map((row, i) => {
              const key = row.tier === 'action' ? `a-${row.action.kind}` : `c-${row.contact.contactId}`
              return (
                <li key={key}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === cursor}
                    className={`palette__row${i === cursor ? ' palette__row--cursor' : ''}`}
                    data-testid={`palette-row-${row.tier}`}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => run(row)}
                  >
                    {row.tier === 'action' ? (
                      <span className="palette__action">{ACTION_LABEL[row.action.kind]}</span>
                    ) : (
                      <>
                        <span className="palette__name">
                          {row.contact.displayName ?? row.contact.phoneNumber ?? 'Unknown Contact'}
                        </span>
                        {/* ⚠️ THE NUMBER IS THE DISAMBIGUATOR AND IT IS NOT DECORATION.
                            Two people share a name often enough that the fixture has a
                            duplicate pair; without a second field they are one row twice. */}
                        {row.contact.displayName !== null && row.contact.phoneNumber !== null && (
                          <span className="palette__meta">{row.contact.phoneNumber}</span>
                        )}
                        {row.contact.conversationId === null && (
                          <span className="palette__meta" data-testid="palette-no-thread">
                            No Conversation Yet
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        ) : (
          /*
            ⛔ THREE SENTENCES, NOT ONE. `paletteEmpty` decides which; rendering
            "nothing found" for a two-letter query tells somebody their colleague
            does not exist because they have not finished typing.
          */
          <p className="palette__empty" data-testid={`palette-${empty.kind}`}>
            {empty.kind === 'tooShort'
              ? `Type at least ${MIN_QUERY} characters`
              : empty.kind === 'searching'
                ? 'Searching'
                : 'Nothing matches that'}
          </p>
        )}

        {/*
          ⛔ THE HANDOFF ROW BELONGS HERE AND IS ABSENT UNTIL ITEM 22 EXISTS.
          Build_Order:486 wants a last row that always hands off to full search.
          There is no /search route to hand off TO, and an inert row is a dead
          affordance. Named gap, per item 11's rule.
        */}
      </div>
    </div>
  )
}
